import { Button, Form, Input, Select } from 'antd'
import { useEffect, useMemo, useRef, useState } from 'react'
import api from 'src/api/index.api'
import { AIPromptPreviewModal, ButtonCommon } from 'src/common'
import { useAppDispatch, useAppSelector, usePromptPreview } from 'src/hooks'
import { onCreateMeaning } from 'src/redux/definition/definition.actions'
import {
	selectCurrentDictWord,
	selectCurrentDictWordIDFromDef,
} from 'src/redux/definition/definition.selector'
import { onGetAllDictWordTypes } from 'src/redux/dict/dict.action'
import { selectDictWordTypes } from 'src/redux/dict/dict.selector'
import { handleSubmitCb, showMessageError, showMessageSuccess } from 'src/utils/function.utils'

const { TextArea } = Input
// const { Option } = Select

export const FormAddMeaningItem = ({
	initialTypeID,
	lang,
	/** When true, do not pre-fill or auto-pick a part of speech — user must choose Type. */
	skipTypeDefault = false,
}) => {
	const dispatch = useAppDispatch()
	const dictWordID = useAppSelector(selectCurrentDictWordIDFromDef)
	const currentDictWord = useAppSelector(selectCurrentDictWord)
	const dictWordTypes = useAppSelector(selectDictWordTypes)
	const inputRef = useRef()
	const [form] = Form.useForm()
	const [isGeneratingDefinition, setIsGeneratingDefinition] = useState(false)
	const [isPreviewingPrompt, setIsPreviewingPrompt] = useState(false)
	const { preview: promptPreview, openPreview, closePreview } =
		usePromptPreview()

	const wordTypeOptions = useMemo(() => {
		const rs = []
		dictWordTypes?.map(item => {
			if (item.isTypeLanguage === lang) {
				rs.push({
					label: item.type,
					value: item.id,
				})
			}
			return rs
		})

		return rs
	}, [dictWordTypes, lang])

	useEffect(() => {
		inputRef.current.focus()
	}, [])

	useEffect(() => {
		if (!dictWordTypes || dictWordTypes.length <= 0) {
			dispatch(
				onGetAllDictWordTypes({
					// pageNum: 0,
					// pageSize: 3443,
				})
			)
		}
	}, [dispatch, dictWordTypes])

	useEffect(() => {
		if (
			!skipTypeDefault &&
			(initialTypeID == null || initialTypeID === '') &&
			wordTypeOptions?.length > 0
		) {
			form.setFieldsValue({
				dictWordTypeID: wordTypeOptions[0].value,
			})
		}
	}, [initialTypeID, wordTypeOptions, form, skipTypeDefault])

	const handleSubmit = async () => {
		const values = await form.validateFields()

		dispatch(
			onCreateMeaning(
				{
					dictWordID,
					...values, // dictWordTypeID, definition
				},
				handleSubmitCb
			)
		)
	}

	const handleGenerateDefinitionWithAI = async () => {
		const context = getDefinitionAIPromptContext()
		if (!context) return

		try {
			setIsGeneratingDefinition(true)
			const rs = await api.agentsAdminApi.generateDictionaryDefinition({
				word: context.word,
				wordType: context.wordType,
				lang,
			})
			const generatedDefinition = (rs?.definition || '').trim()
			if (!generatedDefinition) {
				showMessageError('AI returned empty definition')
				return
			}
			form.setFieldValue('definition', generatedDefinition)
			showMessageSuccess('Generated definition successfully')
		} catch (err) {
			showMessageError(
				err?.response?.data || err?.message || 'Failed to generate definition'
			)
		} finally {
			setIsGeneratingDefinition(false)
		}
	}

	const getDefinitionAIPromptContext = () => {
		const word = (currentDictWord?.word || '').trim()
		if (!word) {
			showMessageError('Word is required before generating definition')
			return null
		}
		const selectedTypeID = form.getFieldValue('dictWordTypeID')
		const selectedType = wordTypeOptions.find(
			item => Number(item.value) === Number(selectedTypeID)
		)
		if (!selectedType?.label) {
			showMessageError('Please select Type before generating definition')
			return null
		}
		return {
			word,
			wordType: selectedType.label,
		}
	}

	const handlePreviewPrompt = async () => {
		const context = getDefinitionAIPromptContext()
		if (!context) return

		try {
			setIsPreviewingPrompt(true)
			const rs =
				await api.agentsAdminApi.previewGenerateDictionaryDefinitionPrompt({
					word: context.word,
					wordType: context.wordType,
					lang,
				})
			openPreview({
				prompt: rs?.prompt || '',
				model: rs?.model || '',
				temperature: rs?.temperature || 0,
				maxOutputTokens: rs?.maxOutputTokens || 0,
			})
		} catch (err) {
			showMessageError(
				err?.response?.data || err?.message || 'Failed to preview prompt'
			)
		} finally {
			setIsPreviewingPrompt(false)
		}
	}

	const typeInitial =
		initialTypeID != null && initialTypeID !== ''
			? Number(initialTypeID)
			: undefined

	return (
		<Form
			form={form}
			initialValues={{
				dictWordTypeID: skipTypeDefault ? undefined : typeInitial,
			}}
		>
			<Form.Item
				label="Definition"
				name="definition"
				rules={[
					{
						required: true,
						message: 'Please enter example',
					},
				]}
			>
				<TextArea ref={inputRef} placeholder={`Enter definition`} />
			</Form.Item>
			<div className="u-row-right" style={{ marginTop: -10, marginBottom: 8 }}>
				<Button
					size="small"
					type="default"
					style={{ marginRight: 8 }}
					loading={isPreviewingPrompt}
					onClick={handlePreviewPrompt}
				>
					Preview AI prompt
				</Button>
				<Button
					size="small"
					type="default"
					loading={isGeneratingDefinition}
					onClick={handleGenerateDefinitionWithAI}
				>
					Generate definition with AI
				</Button>
			</div>
			<Form.Item
				label="Type"
				name="dictWordTypeID"
				rules={[
					{
						required: true,
						message: 'Please select value',
					},
				]}
			>
				<Select
					options={wordTypeOptions}
					placeholder="Select type"
					showSearch
					optionFilterProp="children"
					filterOption={(input, options) =>
						(options.label ?? '')
							.toLowerCase()
							.includes(input.toLowerCase())
					}
				></Select>
			</Form.Item>
			<div className="u-row-right">
				<ButtonCommon onClick={handleSubmit} type="primary">
					Save
				</ButtonCommon>
			</div>
			<AIPromptPreviewModal
				visible={promptPreview.visible}
				onCancel={closePreview}
				prompt={promptPreview.prompt}
				model={promptPreview.model}
				temperature={promptPreview.temperature}
				maxOutputTokens={promptPreview.maxOutputTokens}
			/>
		</Form>
	)
}
